import { BonusComparisonItem, CadenceLegendItem, ExampleLineGroup, HowStep } from '../types/howItWorks';

export const bonusComparison: BonusComparisonItem[] = [
  { name: 'Alex Rivera', branch: 'Troy', units: 7, result: '+25 bps', ruleKey: 'V1-MB-3 · V1-MB-CAP', reason: '7+ closed loans → +50 capped to +25 (base 125, 150 cap)', applied: true },
  {
    name: 'Morgan Brooks',
    branch: 'Miami',
    units: 8,
    result: '0 bps',
    ruleKey: 'Bonus switch: Off (NEW)',
    reason: 'Bonus switch off — would be +50 (7+ closed loans), but base 150 leaves no room under the 150 cap',
    applied: false,
  },
  { name: 'Taylor Ortiz', branch: 'Arizona', units: 6, result: '0 bps', ruleKey: 'Bonus switch: Off (NEW)', reason: 'Would be +25 bps (6 closed loans) — per-employee Bonus switch is off', applied: false },
];

export const lineExampleGroups: ExampleLineGroup[] = [
  {
    id: 'standard',
    title: 'LN-2026-004812 · J. Sample',
    subtitle: 'This loan — Alex Rivera, $412,500.00 · Adjusted Loan Amount $385,692.31',
    rows: [
      { payee: 'Alex Rivera', component: 'Base', ruleKey: 'V1-BT-TROY-2', bps: '125', pay: 5156.25, adjusted: 4821.15, status: 'paid' },
      {
        payee: 'Alex Rivera',
        component: 'Monthly Bonus',
        ruleKey: 'V1-MB-3',
        bps: '+25',
        pay: 1031.25,
        adjusted: 964.23,
        status: 'paid',
        note: '+50 capped to +25 — base + bonus can’t exceed 150 bps (Loan Officer fees only)',
      },
      {
        payee: 'Sam Patel',
        component: 'Team Lead row',
        ruleKey: 'V1-TL-RATE',
        bps: '10',
        pay: 412.5,
        adjusted: 385.69,
        status: 'paid',
        note: 'from employee record: Team Lead BPS (Example data) · no bonus BPS on TL / Director rows',
      },
    ],
  },
  {
    id: 'selfgen',
    title: 'LN-2026-004866 · Sam Patel’s own loan',
    subtitle: 'Self-gen — Opportunity Owner = Team Leader, $350,000.00 (adjusted = raw)',
    rows: [
      { payee: 'Sam Patel', component: 'Base', ruleKey: 'V1-BT-TROY-1', bps: '100', pay: 3500, adjusted: 3500, status: 'paid' },
      {
        payee: 'Sam Patel',
        component: 'Monthly Bonus',
        ruleKey: 'V1-MB-1',
        bps: '+15',
        pay: 525,
        adjusted: 525,
        status: 'paid',
        note: '4 closed loans → +15. Differs from main (main gives 0 when LO = loan’s Team Leader); Zeyad confirms bonus applies.',
      },
      { payee: 'Sam Patel', component: 'Team Lead row', ruleKey: 'V1-TL-RATE', bps: '—', pay: null, adjusted: null, status: 'skipped', note: 'Skipped — self-gen' },
    ],
  },
  {
    id: 'split',
    title: 'LN-2026-004790 · K. Mockley',
    subtitle: 'Split 60 / 40 between Alex Rivera and Jordan Lee, $524,000.00 · Adjusted Loan Amount $512,969.23',
    rows: [
      { payee: 'Alex Rivera', component: 'Base · 60%', ruleKey: 'V1-BT-TROY-2', bps: '125', pay: 3930, adjusted: 3847.27, status: 'paid' },
      { payee: 'Alex Rivera', component: 'Monthly Bonus · 60%', ruleKey: 'V1-MB-3', bps: '+25', pay: 786, adjusted: 769.45, status: 'paid', note: '+50 capped to +25 (150 cap)' },
      {
        payee: 'Jordan Lee',
        component: 'Base · 40%',
        ruleKey: 'V1-BT-TROY-2',
        bps: '125',
        pay: 2620,
        adjusted: 2564.85,
        status: 'paid',
        note: 'Jordan: Troy, period volume $1,640,000.00 ≥ $1.5M → base 125',
      },
      { payee: 'Jordan Lee', component: 'Monthly Bonus · 40%', ruleKey: 'V1-MB-1', bps: '+15', pay: 314.4, adjusted: 307.78, status: 'paid' },
    ],
  },
  {
    id: 'manual',
    title: 'Manual line',
    subtitle: 'A fixed amount entered manually — a reason is required',
    rows: [
      { payee: 'Taylor Ortiz', component: 'Manual', ruleKey: 'MANUAL', bps: 'Fixed', pay: 210, adjusted: 210, status: 'manual', note: 'Reason: “Referral credit missed in September”' },
    ],
  },
];

export const cadenceLegend: CadenceLegendItem[] = [
  { cadence: 'monthly', description: 'Base BPS — from branch + this month’s loan volume · Monthly bonus — from this month’s closed loan count' },
  { cadence: 'loan', description: 'Plan version, Adjusted Loan Amount and commission lines' },
  { cadence: 'period', description: 'Monthly roll-up, lock and adjustments' },
];

export const howSteps: HowStep[] = [
  {
    id: 'funds',
    number: 1,
    title: 'Loan funds',
    shortTitle: 'Loan funds',
    cadence: 'loan',
    inputs: [
      { label: 'Loan', value: 'LN-2026-004812 · J. Sample' },
      { label: 'Loan amount', value: '$412,500.00' },
      { label: 'Funded', value: 'Oct 14 2026' },
      { label: 'LO', value: 'Alex Rivera' },
      { label: 'Branch', value: 'Troy' },
      { label: 'Comp type · Product', value: 'Correspondent · not HELOC' },
    ],
    rule: { summary: 'The funded date decides which monthly commission period the loan belongs to.', ruleKeys: [] },
    output: { label: 'Lands in', value: 'October 2026', detail: 'Alex Rivera’s period · Open' },
    explanation: [
      'Nothing is calculated until a loan funds. The funded date — not the application or closing date — puts the loan in a monthly commission period.',
      'Oct 14 2026 falls in October 2026, which is still Open, so the loan’s lines can be recalculated until the month closes.',
    ],
    link: { label: 'See this on the loan', screen: 'loan' },
  },
  {
    id: 'plan',
    number: 2,
    title: 'Pick the plan version',
    shortTitle: 'Plan version',
    cadence: 'loan',
    inputs: [
      { label: 'Funded date', value: 'Oct 14 2026' },
      { label: 'LO · Branch', value: 'Alex Rivera · Troy' },
    ],
    rule: { summary: 'Use the plan version Active on the funded date, then the most specific matching rule.', ruleKeys: ['v1 parity'] },
    newNote: 'Plan versions, effective dates and rule priority (Employee > Branch/Channel > Plan default) are proposed rebuild features.',
    output: { label: 'Plan', value: 'LO Standard Plan v1 parity', detail: 'Plan defaults apply to Alex' },
    explanation: [
      'v1 parity reproduces how Salesforce main calculates today. The v2 draft is a proposal with an effective period of TBD, so it never applies to October loans.',
      'In the rebuild, the most specific rule wins: an Employee rule beats a Branch / Channel rule, which beats the plan default. Divisor rules are matched by Compensation Type and Loan Product.',
      'Every commission line stores a snapshot of the plan version and the inputs it used (loan amount, units, base BPS, bonus BPS, branch), so a line can always be explained later — even after the rules change.',
    ],
    link: { label: 'See the rules', screen: 'plans' },
  },
  {
    id: 'base',
    number: 3,
    title: 'Base BPS (branch + period volume)',
    shortTitle: 'Base BPS',
    cadence: 'monthly',
    inputs: [
      { label: 'Branch', value: 'Troy' },
      { label: 'October 2026 loan volume', value: '$2,485,400.00' },
    ],
    rule: {
      summary: 'Standard LO, by branch and the employee’s period loan volume: Troy 100, or 125 at ≥ $1.5M · Miami 100, or 150 at ≥ $2M · Arizona flat 100 · other or blank branch 0.',
      ruleKeys: ['V1-BT-TROY-2', 'V1-BT-UNLIC'],
    },
    output: { label: 'Base BPS', value: '125 bps', detail: 'Troy · $2,485,400.00 ≥ $1,500,000.00' },
    explanation: [
      'Alex is in Troy and his October volume is $2,485,400.00, which is at least $1,500,000.00 → 125 bps on every October loan.',
      'Custom-commission employees (Drew Kim) use their Custom Base BPS (120 bps). Team Lead and Director rows use Team Lead BPS / Director BPS from the employee record. Unlicensed LOs are Standard 100 bps.',
      'Tier labels are only a quarter snapshot for display and do not affect pay.',
    ],
    link: { label: 'See the rules', screen: 'plans' },
  },
  {
    id: 'bonus',
    number: 4,
    title: 'Monthly bonus (set monthly)',
    shortTitle: 'Monthly bonus',
    cadence: 'monthly',
    inputs: [
      { label: 'October 2026 closed loans', value: '7' },
      { label: 'Bonus switch', value: 'On' },
    ],
    rule: {
      summary: 'This month’s closed loans: 0–3 = none · 4 = +15 · 5 = +15 · 6 = +25 · 7+ = +50 bps, Loan Officer fees only. Base + bonus can’t exceed 150 bps.',
      ruleKeys: ['V1-MB-3', 'V1-MB-CAP'],
    },
    output: { label: 'Bonus', value: '+25 bps', detail: '7 closed loans → +50 capped to +25 (125 + 25 = 150 cap)' },
    explanation: [
      'The bonus counts this month’s closed loans and applies to every loan closed that month — including loans that closed before the LO reached the band. It applies to Loan Officer fees only; Team Lead and Director rows carry no bonus BPS.',
      'Cap: bonus = MIN(bonus, 150 − base). Alex’s base is 125, so his +50 is capped to +25. git main CASE pays 0 at 4–6; prod to be confirmed.',
      'Team Leads keep the bonus on their own self-gen loans (Sam Patel 4 closed loans → +15; Quinn Harper 3 → none). Differs from main (main gives 0 when LO = loan’s Team Leader); Zeyad confirms bonus applies.',
      'A per-employee Bonus switch (NEW — not in main today) can turn the bonus off for one person; Morgan Brooks and Taylor Ortiz have it off in the example data.',
    ],
    link: { label: 'See the rules', screen: 'plans' },
  },
  {
    id: 'adjusted',
    number: 5,
    title: 'Adjusted Loan Amount',
    shortTitle: 'Adjusted amount',
    cadence: 'loan',
    inputs: [
      { label: 'Total Comp Corr %', value: '3.10%' },
      { label: 'Processing collected', value: '$795.00' },
      { label: 'Underwriting collected', value: '$1,490.00' },
      { label: 'Comp type · Product', value: 'Correspondent · not HELOC' },
    ],
    rule: {
      summary: 'Subtract the halved fee short from comp %. If it is below the divisor, Loan × ABS(comp %) ÷ divisor; otherwise the raw loan amount. No cap or floor.',
      ruleKeys: ['V1-FS-THRESH', 'V1-FS-VA', 'V1-DIV-CORR'],
    },
    output: { label: 'Adjusted Loan Amount', value: '$385,692.31', detail: 'The basis for the Adjusted amount that pays' },
    explanation: [
      'Fee short = (Target − Processing − UW/Admin) ÷ 2. Target is $2,790.00, or $555.00 for a VA Interest Rate Reduction refinance. It applies to Correspondent, non-HELOC loans only; Broker and HELOC loans have no fee short. Alex’s loan: ($2,790.00 − $795.00 − $1,490.00) ÷ 2 = $252.50.',
      'The divisor is chosen by Compensation Type and Loan Product (Correspondent 3.25%, non-correspondent HELOC 3.25%, Broker non-HELOC 2.75%) — never by branch. A negative comp % still produces an amount because main uses ABS(); there is no floor.',
    ],
    link: { label: 'See this on the loan', screen: 'loan' },
  },
  {
    id: 'lines',
    number: 6,
    title: 'Commission lines are created',
    shortTitle: 'Commission lines',
    cadence: 'loan',
    inputs: [
      { label: 'Base + bonus', value: '125 + 25 (capped from 50) = 150 bps' },
      { label: 'Adjusted Loan Amount', value: '$385,692.31' },
    ],
    rule: {
      summary: 'One row per payee: Loan Officer always (base + bonus, capped at 150 bps); Team Lead unless Owner = Team Leader; Director unless Owner = Division Leader.',
      ruleKeys: ['V1-BT-TROY-2', 'V1-MB-3', 'V1-TL-RATE', 'V1-DIR-RATE'],
    },
    newNote: 'One line per payee × component, splits and manual lines with a reason are proposed. Today there is one fee row per payee per loan.',
    output: {
      label: 'Paid lines on this loan',
      value: '$6,171.07 adjusted',
      detail: 'Alex $5,785.38 + Sam Patel Team Lead row $385.69 · $6,600.00 stored (unadjusted) – reference',
    },
    explanation: [
      'Team Lead and Director rows use the BPS on the employee record (Team Lead BPS / Director BPS): Adjusted Loan Amount × BPS ÷ 10,000, with no bonus BPS. The Director row writes to the Opportunity’s Division Leader Commissions field.',
      'Each row pays its Adjusted amount (Adjusted Loan Amount × BPS). Stored (unadjusted) = Loan Amount × BPS is shown for reference only.',
    ],
    link: { label: 'See this on the loan', screen: 'loan' },
  },
  {
    id: 'rollup',
    number: 7,
    title: 'Monthly roll-up',
    shortTitle: 'Monthly roll-up',
    cadence: 'period',
    inputs: [
      { label: 'Alex’s October loans', value: '7 loans · 14 lines' },
      { label: 'Carried-in adjustments', value: '2 lines' },
    ],
    rule: { summary: 'The period total is the sum of the LO’s lines for the month. Nothing is typed into totals.', ruleKeys: [] },
    output: { label: 'October 2026 total', value: '$30,380.32 adjusted', detail: '$34,017.00 stored (unadjusted) – reference · frozen at the moment October locks' },
    explanation: [
      'The period total that pays is the sum of the Adjusted amounts on the LO’s lines. The Stored (unadjusted) sum is shown for reference only.',
      'Alex’s 16 October lines: 14 loan lines plus 2 adjustments carried in from locked months. This is the total at the moment of lock; anything that changes afterwards posts to November (step 8).',
    ],
    link: { label: 'See October statement', screen: 'statement' },
  },
  {
    id: 'lock',
    number: 8,
    title: 'Period locks → paycheck',
    shortTitle: 'Lock → paycheck',
    cadence: 'period',
    inputs: [
      { label: 'September 2026', value: 'Locked' },
      { label: 'October 2026', value: 'Open → being closed now' },
      { label: 'November 2026', value: 'Open next' },
    ],
    rule: { summary: 'Locking freezes every employee’s month. Later changes become Adjustment lines in the next open month.', ruleKeys: [] },
    newNote: 'Period locking and adjustments posted to the next open period are proposed. Today a post-funding loan edit silently rewrites the original month’s fee.',
    output: { label: 'Paycheck', value: 'Locked October total', detail: 'Adjusted amount pays · later changes → November' },
    explanation: [
      'When a month is locked, its lines become read-only and the locked Adjusted total is the basis for the paycheck. Unlocking needs an admin and a reason.',
      'A change to a loan in a locked month is never an edit to that month. The difference posts as an Adjustment line in the next open month — that’s how Alex’s October picked up +$120.00 from September and −$240.00 from August. Once October locks, the same thing happens with November.',
    ],
    link: { label: 'See October statement', screen: 'statement' },
  },
];
